import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
  });

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [tempToken, setTempToken] = useState("");

  const { login, verifyMfa, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result?.mfa_required) {
          setTempToken(result.temp_token);
          setMfaStep(true);
          setLoading(false);
          return;
        }
        toast.success("Welcome back!");
      } else {
        if (!formData.fullName.trim()) {
          toast.error("Please enter your full name");
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.fullName);
        // Fire Meta Pixel CompleteRegistration event
        if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: formData.fullName });
        toast.success("Account created successfully!");
      }
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (mfaCode.length !== 6) {
      toast.error("Please enter your 6-digit code");
      return;
    }
    setLoading(true);
    try {
      await verifyMfa(tempToken, mfaCode);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
      setMfaCode("");
    } finally {
      setLoading(false);
    }
  };

  // MFA verification screen
  if (mfaStep) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img src="/logo_compact.png" alt="Opturna Tax AI" className="h-12 rounded-lg" data-testid="auth-logo" />
            </div>
          </div>

          <Card className="border-stone-200 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <CardTitle className="text-2xl font-bold text-stone-900">Two-Factor Authentication</CardTitle>
              <CardDescription className="text-stone-600">
                Enter the 6-digit code from your authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleMfaVerify} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm text-center block text-stone-600">Verification Code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                    autoFocus
                    data-testid="mfa-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || mfaCode.length !== 6}
                  className="w-full h-12 bg-[#1e3a5f] hover:bg-[#2d5a8e] text-white rounded-lg font-medium"
                  data-testid="mfa-verify-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Verifying...
                    </span>
                  ) : "Verify & Sign In"}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => { setMfaStep(false); setMfaCode(""); setTempToken(""); }}
                  className="text-sm text-stone-500 hover:text-[#1e3a5f]"
                  data-testid="mfa-back-btn"
                >
                  Back to login
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <img src="/logo_compact.png" alt="Opturna Tax AI" className="h-12 rounded-lg" data-testid="auth-logo" />
          </div>
        </div>

        <Card className="border-stone-200 shadow-xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-bold text-stone-900">
              {isLogin ? "Welcome Back" : "Create Account"}
            </CardTitle>
            <CardDescription className="text-stone-600">
              {isLogin
                ? "Sign in to manage your business finances"
                : "Start your Opturna journey today"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-stone-700">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="John Doe"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="h-12"
                    data-testid="fullname-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12"
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-stone-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="h-12"
                  data-testid="password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#1e3a5f] hover:bg-[#2d5a8e] text-white rounded-lg font-medium"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </span>
                ) : isLogin ? "Sign In" : "Create Account"}
              </Button>
            </form>

            {!isLogin && (
              <p className="mt-4 text-xs text-center text-stone-500" data-testid="auth-terms-notice">
                By creating an account, you agree to our{" "}
                <a href="/terms" className="text-[#4a9eff] hover:underline">Terms of Service</a> and{" "}
                <a href="/privacy" className="text-[#4a9eff] hover:underline">Privacy Policy</a>.
              </p>
            )}

            <div className="mt-6 text-center">
              <p className="text-stone-600">
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="ml-2 text-[#1e3a5f] hover:text-[#2d5a8e] font-medium"
                  data-testid="toggle-auth-mode"
                >
                  {isLogin ? "Sign up" : "Sign in"}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
